import { useCallback, useEffect, useState } from 'react'

import { api, can, type ConnectorInstance, type ConnectorTemplate } from '../../lib/api'
import { ErrorBox, Loading } from '../../components/DataTable'
import { Status, Toggletip, type Tone } from '../../components/kit'
import { PROOF_KEYS } from '../../lib/entraProof'
import { OffboardingProof } from './OffboardingProof'
import { PersonaMappings } from './PersonaMappings'
import { mfaApi, type SignInSecurity } from '../../lib/mfa'
import '../../styles/sweep.css'

// What the API sends in place of a stored credential. Sent back unchanged it
// means "keep it", so both ends have to agree on the exact string.
const MASK = '········'

// Identity and single sign-on, arranged as the consent product arranges it: a
// protocol picked first, then only the fields that protocol needs.
//
// # WHY THE PROVIDER IS PICKED BEFORE ANYTHING IS TYPED
//
// Because Entra ID and Cognito are both OpenID Connect and neither asks for an
// issuer: one is assembled from a directory id and the other from a region and
// a pool. Showing an issuer field and expecting somebody to construct it is how
// half of these are configured wrong, and the failure arrives later as a
// sign-in loop rather than here as a bad value.
//
// # WHY THE REASONS SIT BEHIND AN (i)
//
// Each field keeps the one line somebody needs to fill it in; why it is asked
// and what follows from it open beside that line, so the fields that decide
// who can sign in are not buried under their own explanations.

type Kind = 'oidc_generic' | 'entra_id' | 'cognito'

const KINDS: { key: Kind; label: string; what: string }[] = [
  {
    key: 'entra_id',
    label: 'Microsoft Entra ID',
    what: 'Azure AD, the directory id and an application registered in it',
  },
  {
    key: 'cognito',
    label: 'Amazon Cognito',
    what: 'A user pool, as the hosted deployments use or a customer brings their own',
  },
  {
    key: 'oidc_generic',
    label: 'Any OpenID Connect provider',
    what: 'Okta, Keycloak, Google Workspace, ADFS or any provider with a discovery document',
  },
]

export function IdentitySso() {
  const [templates, setTemplates] = useState<Record<Kind, ConnectorTemplate> | null>(null)
  const [instance, setInstance] = useState<ConnectorInstance | null>(null)
  const [kind, setKind] = useState<Kind>('entra_id')
  const [form, setForm] = useState<Record<string, string>>({})
  const [dirty, setDirty] = useState(false)
  const [busy, setBusy] = useState('')
  const [issues, setIssues] = useState<string[]>([])
  const [error, setError] = useState<unknown>(null)
  const [probe, setProbe] = useState<{ allOk: boolean; note: string; checks: { check: string; ok: boolean; detail: string; unproven?: boolean }[] } | null>(null)

  const load = useCallback(() => {
    api
      .connectors()
      .then((v) => {
        const g = v.groups.find((x) => x.category === 'IDENTITY')
        if (!g) return
        const byKey = {} as Record<Kind, ConnectorTemplate>
        let found: ConnectorInstance | null = null
        let foundKey: Kind = 'entra_id'
        for (const item of g.items) {
          byKey[item.template.key as Kind] = item.template
          if (item.instance) {
            found = item.instance
            foundKey = item.template.key as Kind
          }
        }
        setTemplates(byKey)
        setInstance(found)
        setKind(foundKey)
        const start: Record<string, string> = {}
        for (const f of byKey[foundKey]?.fields ?? []) {
          const held = found?.config?.[f.key]
          start[f.key] = held !== undefined && held !== null ? String(held) : String(f.default ?? '')
        }
        setForm(start)
        setDirty(false)
        setError(null)
      })
      .catch(setError)
  }, [])
  useEffect(load, [load])

  // Reads the card again without touching the form, for the proof section:
  // a person halfway through editing sign-in keeps what they typed.
  const refreshCard = useCallback(() => {
    api
      .connectors()
      .then((v) => {
        const g = v.groups.find((x) => x.category === 'IDENTITY')
        setInstance(g?.items.find((item) => item.instance)?.instance ?? null)
      })
      .catch(() => {
        /* the next full load shows the card as it is */
      })
  }, [])

  if (error != null) return <ErrorBox error={error} what="Identity" />
  if (!templates) return <Loading what="the identity configuration" />

  const mayWrite = can('config.write')
  const template = templates[kind]

  function set(k: string, v: string) {
    setForm((old) => ({ ...old, [k]: v }))
    setDirty(true)
  }

  function pick(k: Kind) {
    setKind(k)
    setDirty(true)
    const start: Record<string, string> = {}
    for (const f of templates![k]?.fields ?? []) start[f.key] = String(f.default ?? '')
    setForm(start)
    setProbe(null)
  }

  async function save() {
    setBusy('save')
    setIssues([])
    const body: Record<string, unknown> = {}
    for (const f of template?.fields ?? []) {
      // The proof's settings are saved by their own section. This save sends
      // the whole card, so it sends them as the card holds them now (a stored
      // secret as the mask, which keeps it) rather than as this form loaded
      // them, which would undo a proof saved since.
      if (PROOF_KEYS.includes(f.key)) {
        const held = instance && instance.templateKey === kind ? instance.config?.[f.key] : undefined
        if (held !== undefined && held !== null && String(held) !== '') body[f.key] = String(held)
        continue
      }
      const raw = (form[f.key] ?? '').trim()
      if (raw !== '') body[f.key] = raw
    }
    try {
      if (instance && instance.templateKey !== kind) {
        await api.removeConnector(instance.id)
        await api.addConnector(kind, body)
      } else if (instance) {
        await api.saveConnector(instance.id, body)
      } else {
        await api.addConnector(kind, body)
      }
      setDirty(false)
      setProbe(null)
      load()
    } catch (err) {
      const any = err as { issues?: { detail: string }[]; message?: string }
      setIssues(any.issues?.map((i) => i.detail) ?? [any.message ?? 'That was not saved.'])
    } finally {
      setBusy('')
    }
  }

  return (
    <div className="settings-stack">
      {/* What saving here actually does, said before it is done rather than
          discovered afterwards. It changes who can sign in, which is the one
          setting on these screens that can lock somebody out. */}
      <div className="note">
        <strong>This decides who signs in</strong>
        Once proved, people sign in through this provider within a minute, no restart{' '}
        <Toggletip label="What proving the connection changes">
          The tokens it issues are then the ones accepted. Until it is proved, sign-in carries on as it does today, and
          pausing or removing it puts things back within a minute.
        </Toggletip>
      </div>

      <section className="panel">
        <div className="p1-head">
          <div>
            <h2>Identity provider</h2>
            <p className="obs-what">Who signs in, and the roles the provider returns for them</p>
          </div>
          {instance && (
            <div className="std-meta">
              {instance.status === 'ACTIVE' ? (
                <Status tone="ok">Answering</Status>
              ) : instance.status === 'FAILING' ? (
                <Status tone="warn">Not answering</Status>
              ) : instance.status === 'PAUSED' ? (
                <Status tone="neutral">Paused</Status>
              ) : (
                <Status tone="neutral">Not proved</Status>
              )}
            </div>
          )}
        </div>

        <div className="field-block">
          <span className="field-label">Provider</span>
          <div className="method-grid">
            {KINDS.map((k) => (
              <button
                key={k.key}
                type="button"
                className={'method-card' + (kind === k.key ? ' on' : '')}
                disabled={!mayWrite}
                onClick={() => pick(k.key)}
              >
                <strong>{k.label}</strong>
                <span>{k.what}</span>
              </button>
            ))}
          </div>
        </div>

        {kind === 'entra_id' && (
          <div className="field-row two">
            <label>
              Directory (tenant) id
              <input value={form.tenantId ?? ''} onChange={(e) => set('tenantId', e.target.value)} disabled={!mayWrite} />
            </label>
            <label>
              Application (client) id
              <input value={form.clientId ?? ''} onChange={(e) => set('clientId', e.target.value)} disabled={!mayWrite} />
            </label>
          </div>
        )}

        {kind === 'cognito' && (
          <div className="field-row three">
            <label>
              Region
              <input value={form.region ?? 'ap-south-1'} onChange={(e) => set('region', e.target.value)} disabled={!mayWrite} />
            </label>
            <label>
              User pool id
              <input
                value={form.userPoolId ?? ''}
                onChange={(e) => set('userPoolId', e.target.value)}
                placeholder="ap-south-1_XXXXXXXXX"
                disabled={!mayWrite}
              />
            </label>
            <label>
              App client id
              <input value={form.clientId ?? ''} onChange={(e) => set('clientId', e.target.value)} disabled={!mayWrite} />
            </label>
          </div>
        )}

        {kind === 'oidc_generic' && (
          <>
            <div className="field-row two">
              <label>
                Issuer URL
                <input
                  value={form.issuer ?? ''}
                  onChange={(e) => set('issuer', e.target.value)}
                  placeholder="https://login.example.com/realms/main"
                  disabled={!mayWrite}
                />
                <span className="field-note">Its discovery document is read from here, so nothing else has to be typed</span>
              </label>
              <label>
                Client id
                <input value={form.clientId ?? ''} onChange={(e) => set('clientId', e.target.value)} disabled={!mayWrite} />
              </label>
            </div>
            <div className="field-row two">
              <label>
                Audience
                <input value={form.audience ?? ''} onChange={(e) => set('audience', e.target.value)} disabled={!mayWrite} />
                <span className="field-note">Only where the provider names one separately from the client id</span>
              </label>
              <label>
                Scopes
                <input
                  value={form.scopes ?? 'openid profile email'}
                  onChange={(e) => set('scopes', e.target.value)}
                  disabled={!mayWrite}
                />
              </label>
            </div>
          </>
        )}

        <div className="field-row two">
          <label>
            Roles claim
            <input value={form.rolesClaim ?? 'roles'} onChange={(e) => set('rolesClaim', e.target.value)} disabled={!mayWrite} />
            <span className="field-note">What a person may do: roles in Entra ID, realm_access.roles in Keycloak</span>
          </label>
          <label>
            Client secret
            <input
              type={(form.clientSecret ?? '') === MASK ? 'text' : 'password'}
              value={form.clientSecret ?? ''}
              onFocus={() => {
                if ((form.clientSecret ?? '') === MASK) set('clientSecret', '')
              }}
              onChange={(e) => set('clientSecret', e.target.value)}
              placeholder="Paste it here"
              autoComplete="off"
              disabled={!mayWrite}
            />
            <span className="field-note">
              {(form.clientSecret ?? '') === MASK ? (
                'A secret is stored, click here to replace it'
              ) : (
                <>
                  Stored encrypted and never shown again{' '}
                  <Toggletip label="When no secret is needed">A console signing in with PKCE needs none at all.</Toggletip>
                </>
              )}
            </span>
          </label>
        </div>

        {issues.map((i) => (
          <p key={i} className="note warn">
            {i}
          </p>
        ))}

        {mayWrite && (
          <div className="p1-actions save-row">
            <button
              type="button"
              className="button primary"
              disabled={busy !== '' || !dirty}
              onClick={() => void save()}
              data-action="tprm.settings-sso.a01"
              data-action-also="tprm.settings-sso.a02 tprm.settings-sso.a03 tprm.settings-sso.a04"
            >
              {busy === 'save' ? 'Saving' : 'Save'}
            </button>
            {instance && (
              <button
                type="button"
                className="button"
                disabled={busy !== '' || dirty}
                onClick={async () => {
                  setBusy('probe')
                  try {
                    setProbe(await api.testConnector(instance.id))
                    load()
                  } finally {
                    setBusy('')
                  }
                }}
                data-action="tprm.settings-sso.a05"
              >
                {busy === 'probe' ? 'Checking' : 'Check the provider'}
              </button>
            )}
            {dirty && (
              <span className="save-hint sw-row">
                <Status tone="warn">Unsaved</Status>
                Save before checking
              </span>
            )}
          </div>
        )}

        {probe && (
          <div className={'note' + (probe.allOk ? ' ok' : ' warn')} role="status">
            <strong>{probe.note}</strong>
            <ul className="probe-list">
              {probe.checks.map((c) => {
                const label = checkLabel(c)
                return (
                  <li key={c.check} className={c.unproven ? 'probe-open' : c.ok ? 'probe-ok' : 'probe-no'}>
                    <Status tone={label.tone}>{label.word}</Status>
                    <span>
                      <strong>{c.check}</strong> {c.detail}
                    </span>
                  </li>
                )
              })}
            </ul>
          </div>
        )}
        {!probe && instance?.lastError && <p className="note warn">{instance.lastError}</p>}
      </section>
      <SignInSecurityCard />
      <OffboardingProof card={instance} onCardChanged={refreshCard} />
      <PersonaMappings />
    </div>
  )
}

// A check's answer is one of three, and unproven stays its own label: folding
// it into passed is how a screen claims sign-in works when nobody checked.
function checkLabel(c: { ok: boolean; unproven?: boolean }): { tone: Tone; word: string } {
  if (c.unproven) return { tone: 'warn', word: 'Unproven' }
  if (c.ok) return { tone: 'ok', word: 'Passed' }
  return { tone: 'crit', word: 'Refused' }
}

// Whether administrators must sign in with a second factor (release 3.1). It
// covers TPRM accounts that give people access or change configuration and
// sign in with a TPRM password; single sign-on asks for the organisation's own
// factor and is unaffected. Off until an administrator turns it on, so an
// upgrade locks nobody out; turning it on takes effect at each
// administrator's next password sign-in, which walks them through setting one
// up before any session opens.
function SignInSecurityCard() {
  const [state, setState] = useState<SignInSecurity | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    mfaApi.signInSecurity().then(setState).catch(setError)
  }, [])
  if (error !== null) return <ErrorBox error={error} what="Sign-in security" />
  if (state === null) return <Loading what="sign-in security" />
  if (!state.applies) return null
  const mayWrite = can('config.write')
  async function flip(on: boolean) {
    setBusy(true)
    setError(null)
    try {
      await mfaApi.setSignInSecurity(on)
      setState((s) => (s ? { ...s, requireMfaForAdmins: on } : s))
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }
  return (
    <section className="panel" aria-labelledby="signin-security-heading" style={{ marginTop: 16 }}>
      <div className="panel-head">
        <h2 id="signin-security-heading" style={{ margin: 0 }}>
          Sign-in security
        </h2>
        <Status tone={state.requireMfaForAdmins ? 'ok' : 'neutral'}>
          {state.requireMfaForAdmins ? 'Second factor required for administrators' : 'Second factor optional'}
        </Status>
      </div>
      <div style={{ padding: '12px 16px' }}>
        <label>
          <input
            type="checkbox"
            data-action="tprm.settings-sso.a11"
            checked={state.requireMfaForAdmins}
            disabled={!mayWrite || busy}
            onChange={(e) => void flip(e.target.checked)}
          />{' '}
          Require a second factor for administrators who sign in with a TPRM password{' '}
          <Toggletip label="Who this covers">{state.note}</Toggletip>
        </label>
        <p className="muted" style={{ fontSize: 12, margin: '6px 0 0' }}>
          Everybody else may set one up from the shield in the sidebar. A lost phone is reset in People and roles.
        </p>
      </div>
    </section>
  )
}
